import { CAMPAIGN_NONE, CAMPAIGN_UNKNOWN } from './CampaignFilter'

export interface CampaignTreeOption {
  value: string
  label: string
}

export interface CampaignTreeGroup {
  slug: string
  name: string
  campaigns: CampaignTreeOption[]
}

/** The synthetic group slugs the server emits (see sky_campaign_tree.go). */
export const UNCLAIMED_GROUP = '__unclaimed'
export const NO_PRODUCT_GROUP = '__noproduct'

/** A parent checkbox is on, off, or partly on. */
export type GroupState = 'all' | 'some' | 'none'

/**
 * The campaign tree's selection rules (T3.4).
 *
 * 🔴 THERE IS ONE SELECTION AND IT IS CAMPAIGNS. Product is a GROUPING that
 * WRITES to that selection, never a second selection of its own. The earlier
 * design had both, and Kai identified that they fight: a product IS a set of
 * campaigns, so "products = {Brand}" and "campaigns = {Cinema's}" have no
 * coherent answer. Everything here therefore takes and returns a flat campaign
 * id list, and nothing but campaign ids ever goes on the wire.
 *
 * ✅ This is only well-defined because NO CAMPAIGN MAY BE CLAIMED TWICE — the
 * manifest loader cross-checks it at load. If that rule were relaxed the tree
 * becomes a graph and a parent checkbox has no coherent state.
 */

/** Whether a group is fully, partly, or not selected. An EMPTY group is 'none'. */
export function groupState(group: CampaignTreeGroup, selected: string[]): GroupState {
  if (group.campaigns.length === 0) return 'none'
  const chosen = new Set(selected)
  const hits = group.campaigns.filter((c) => chosen.has(c.value)).length
  if (hits === 0) return 'none'
  return hits === group.campaigns.length ? 'all' : 'some'
}

/**
 * Ticking a parent.
 *
 * Fully selected → clear its children. Otherwise → select ALL of them, which
 * includes the partial case: a half-ticked box completes rather than clears,
 * because the click that produced it was "I want this department" and clearing
 * would discard the children the user had already chosen.
 *
 * Campaigns outside the group are untouched — this writes one group's worth of
 * the single selection and never rewrites the whole thing.
 */
export function toggleGroup(group: CampaignTreeGroup, selected: string[]): string[] {
  const ids = group.campaigns.map((c) => c.value)
  const inGroup = new Set(ids)
  const others = selected.filter((v) => !inGroup.has(v))
  return groupState(group, selected) === 'all' ? others : [...others, ...ids]
}

/** Ticking one leaf. */
export function toggleCampaign(value: string, selected: string[]): string[] {
  return selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
}

/**
 * The wire value for a selection.
 *
 * 🔴 SELECTING EVERYTHING SENDS NO CONSTRAINT, exactly as "all platforms" and
 * "all sources" do. An explicit full list is NOT equivalent: it would still
 * exclude any campaign the tree does not know about — a campaign added to
 * Sprinklr since the manifest was last edited, for instance — so a user who
 * ticked every box would silently see less than a user who ticked none.
 *
 * An explicitly EMPTY selection also sends no constraint, because empty is this
 * page's neutral state (`CampaignFilter` opens neutral and the first click
 * narrows). That differs deliberately from the source control, where empty
 * means "the user un-ticked everything" — there, un-ticking is reachable; here,
 * "nothing selected" IS the opening state and cannot mean "show nothing".
 */
export function campaignsForQuery(groups: CampaignTreeGroup[], selected: string[]): string[] {
  if (selected.length === 0) return []
  const every = groups.flatMap((g) => g.campaigns.map((c) => c.value))
  const chosen = new Set(selected)
  const coversEverything = every.length > 0 && every.every((v) => chosen.has(v))
  return coversEverything ? [] : [...selected].sort()
}

/** True when this option is one of the two reserved sentinels. */
export function isSentinel(value: string): boolean {
  return value === CAMPAIGN_NONE || value === CAMPAIGN_UNKNOWN
}

/**
 * How many campaigns are actually constraining the view — 0 when the selection
 * is neutral OR covers everything, so the badge never claims a filter that
 * changes nothing.
 */
export function activeCampaignCount(groups: CampaignTreeGroup[], selected: string[]): number {
  return campaignsForQuery(groups, selected).length
}
